import React, { useEffect, useState } from 'react';
import disbursementsAPI from '../../api/disbursementsAPI';

const actionStyles = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  POST: 'bg-purple-100 text-purple-800',
  VOID: 'bg-red-100 text-red-800'
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

const formatDateTime = (date) =>
  new Date(date).toLocaleString();

const groupByDate = (logs) => {
  return logs.reduce((acc, log) => {
    const dateKey = formatDate(log.performedAt);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(log);
    return acc;
  }, {});
};

const DisbursementAuditTab = ({ disbursementId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!disbursementId) return;

    const fetchAudit = async () => {
      try {
        setLoading(true);
        const res = await disbursementsAPI.getAuditTrail(disbursementId);
        setLogs(res?.data || []);
      } catch (err) {
        setError('Failed to load audit trail');
      } finally {
        setLoading(false);
      }
    };

    fetchAudit();
  }, [disbursementId]);

  if (loading) return <div className="p-4 text-gray-500">Loading audit trail...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (logs.length === 0) return <div className="p-4 text-gray-500">No audit history</div>;

  const grouped = groupByDate(logs);

  return (
    <div className="space-y-6 p-4">
      {Object.entries(grouped).map(([dateKey, dateLogs]) => (
        <div key={dateKey}>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">{dateKey}</h4>
          <div className="space-y-3">
            {dateLogs.map((log) => (
              <div key={log._id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${actionStyles[log.action] || 'bg-gray-100 text-gray-800'}`}>
                        {log.action}
                      </span>
                      <span className="text-sm text-gray-600">{formatDateTime(log.performedAt)}</span>
                    </div>
                    <p className="text-sm text-gray-700">
                      By: <span className="font-medium">{log.performedBy?.name || 'System'}</span>
                    </p>
                  </div>
                </div>

                {log.action === 'CREATE' && log.snapshot && (
                  <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    <p>Reference: {log.snapshot.referenceNo}</p>
                    <p>Payee: {log.snapshot.payeeSnapshot?.name}</p>
                  </div>
                )}

                {log.action === 'UPDATE' && log.changes && (
                  <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    {Object.entries(log.changes).map(([field, change]) => {
                      const formatValue = (val) => {
                        if (val === null || val === undefined) return 'N/A';
                        if (typeof val === 'object') return JSON.stringify(val);
                        return String(val);
                      };
                      
                      return (
                        <p key={field} className="mb-1">
                          {field}: {formatValue(change.before)} → {formatValue(change.after)}
                        </p>
                      );
                    })}
                  </div>
                )}

                {log.action === 'POST' && log.snapshot && (
                  <div className="mt-2 text-xs text-gray-600 bg-green-50 p-2 rounded">
                    <p>Status: Posted</p>
                    {(log.snapshot.journalEntryNumber || log.snapshot.journalEntryId) && (
                      <p>JE: {log.snapshot.journalEntryNumber || log.snapshot.journalEntryId}</p>
                    )}
                  </div>
                )}

                {log.action === 'VOID' && log.snapshot && (
                  <div className="mt-2 text-xs text-gray-600 bg-red-50 p-2 rounded">
                    <p>Status: Voided</p>
                    <p>Reason: {log.snapshot.voidReason}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DisbursementAuditTab;
